import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  Divider,
  MenuItem,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
// routes
import { PATH_AUTH, PATH_DASHBOARD } from '../../../routes/paths';
// auth
import { useAuthContext } from '../../../auth/useAuthContext';
// redux
import { clearSelectedOrganization } from '../../../redux/slices/organization';
import { useDispatch } from '../../../redux/store';
// components
import { useUserContext } from 'src/auth/useUserContext';
import { ADMIN_APP_URL } from 'src/config-global';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { Roles } from 'src/types/User';
import { IconButtonAnimate } from '../../../components/animate';
import { CustomAvatar } from '../../../components/custom-avatar';
import MenuPopover from '../../../components/menu-popover';
import { useSnackbar } from '../../../components/snackbar';
import { useLocales } from '../../../locales';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    labelKey: 'account.settings',
    linkTo: PATH_DASHBOARD.general.account.general,
    roles: [
      Roles.user,
      Roles.professional,
      Roles.admin,
      Roles.organization_admin,
      Roles.organization_owner,
    ],
  },
  {
    labelKey: 'account.notifications',
    linkTo: PATH_DASHBOARD.general.notifications.invites,
    roles: [
      Roles.organization_admin,
      Roles.organization_owner,
      Roles.professional,
    ],
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { translate } = useLocales();

  const { logout, user } = useAuthContext();
  const { data, isAdminRole } = useUserContext();
  const { isOrganizationNotVerified } = useOrganizationContext();

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      dispatch(clearSelectedOrganization());
      localStorage.removeItem('redux-organization');
      queryClient.removeQueries(['user-organizations']);
      handleClosePopover();
      navigate(PATH_AUTH.login);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(translate('account.logoutError'), {
        variant: 'error',
      });
    }
  };

  const handleClickItem = (path: string) => {
    handleClosePopover();
    navigate(path);
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpenPopover}
        sx={{
          p: 0,
          ...(openPopover && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <CustomAvatar
          src={data?.avatar || user?.photoURL}
          alt={
            data
              ? `${data?.firstName} ${data?.lastName}`
              : `${user?.displayName}`
          }
          name={
            data
              ? `${data?.firstName} ${data?.lastName}`
              : `${user?.displayName}`
          }
        />
      </IconButtonAnimate>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{ minWidth: 200, maxWidth: 400, width: 'auto', p: 0 }}
      >
        <Box sx={{ my: 1.5, px: 2 }}>
          <Typography variant="subtitle2" noWrap>
            {data
              ? `${data?.firstName} ${data?.lastName}`
              : `${user?.displayName}`}
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', wordBreak: 'break-all' }}
          >
            {data?.email ||
              user?.email ||
              data?.phoneNumber ||
              user?.phoneNumber}
          </Typography>
        </Box>

        {pathname !== PATH_DASHBOARD.general.updateAccount &&
          !isOrganizationNotVerified && (
            <Stack sx={{ p: 1 }}>
              {OPTIONS.map((option) => {
                if (
                  option.roles &&
                  data?.roles &&
                  !option.roles.some((role) => data.roles.includes(role))
                ) {
                  return null;
                }

                return (
                  <MenuItem
                    key={option.labelKey}
                    onClick={() => handleClickItem(option.linkTo)}
                  >
                    {translate(option.labelKey)}
                  </MenuItem>
                );
              })}
              {isAdminRole && ADMIN_APP_URL && (
                <MenuItem component="a" href={ADMIN_APP_URL}>
                  {translate('account.adminPanel')}
                </MenuItem>
              )}
            </Stack>
          )}

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          {translate('account.logout')}
        </MenuItem>
      </MenuPopover>
    </>
  );
}
